import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Vote, 
  Users, 
  Calendar, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Trophy,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { Election, Candidate, Vote as VoteType, ElectionResults } from '@/types';

const ElectionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [election, setElection] = useState<Election | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [userVote, setUserVote] = useState<VoteType | null>(null);
  const [results, setResults] = useState<ElectionResults | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<string>('');
  const [showResults, setShowResults] = useState(false);
  const [isAbstaining, setIsAbstaining] = useState(false);

  useEffect(() => {
    if (id) {
      fetchElection();
      fetchCandidates();
      fetchUserVote();
    }
  }, [id]);

  const fetchElection = async () => {
    try {
      const response = await fetch(`/api/v1/elections/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setElection(data.data);
      }
    } catch (error) {
      console.error('Error fetching election:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`/api/v1/elections/${id}/candidates`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCandidates(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchUserVote = async () => {
    try {
      const response = await fetch(`/api/v1/elections/${id}/my-vote`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserVote(data.data);
      }
    } catch (error) {
      console.error('Error fetching user vote:', error);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await fetch(`/api/v1/elections/${id}/results`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data.data);
        setShowResults(true);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const handleVote = async () => {
    if (!selectedCandidate && !isAbstaining) {
      alert('Please select a candidate or choose to abstain');
      return;
    }

    setIsVoting(true);
    try {
      const response = await fetch(`/api/v1/elections/${id}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidate_id: selectedCandidate || null,
          is_abstain: isAbstaining
        })
      });

      if (response.ok) {
        alert('Vote cast successfully!');
        fetchUserVote();
        fetchCandidates();
        setSelectedCandidate('');
        setIsAbstaining(false);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to cast vote');
      }
    } catch (error) {
      console.error('Error casting vote:', error);
      alert('Network error. Please try again.');
    } finally {
      setIsVoting(false);
    }
  };

  const isElectionActive = () => {
    if (!election) return false;
    const now = new Date();
    const startDate = new Date(election.start_date);
    const endDate = new Date(election.end_date);
    return now >= startDate && now <= endDate && election.status === 'active';
  };

  const canVote = () => {
    return isElectionActive() && !userVote && user?.role === 'member';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <Clock className="h-4 w-4" />;
      case 'active': return <Vote className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!election) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Election Not Found</h2>
          <p className="text-gray-600">The requested election could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/app/elections')}
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Elections
            </button>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{election.title}</h1>
              <p className="mt-2 text-gray-600">{election.position}</p>
              {election.description && (
                <p className="mt-2 text-gray-500">{election.description}</p>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(election.status)}`}>
                {getStatusIcon(election.status)}
                <span className="ml-1">{election.status.toUpperCase()}</span>
              </span>
              {isElectionActive() && (
                <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  LIVE
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Election Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Start Date</dt>
                    <dd className="text-sm font-medium text-gray-900">{formatDate(election.start_date)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">End Date</dt>
                    <dd className="text-sm font-medium text-gray-900">{formatDate(election.end_date)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Candidates</dt>
                    <dd className="text-sm font-medium text-gray-900">{candidates.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Voting Section */}
        {canVote() && (
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Cast Your Vote</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select a candidate or choose to abstain from voting.
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {candidates.map((candidate) => (
                  <div
                    key={candidate.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedCandidate === candidate.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setSelectedCandidate(candidate.id);
                      setIsAbstaining(false);
                    }}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="candidate"
                        checked={selectedCandidate === candidate.id}
                        onChange={() => {
                          setSelectedCandidate(candidate.id);
                          setIsAbstaining(false);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <User className="h-8 w-8 text-gray-400 mr-3" />
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {candidate.first_name} {candidate.last_name}
                            </h4>
                            <p className="text-sm text-gray-500">
                              Member ID: {candidate.member_id}
                            </p>
                            {candidate.campaign_slogan && (
                              <p className="text-sm text-gray-600 mt-1">
                                "{candidate.campaign_slogan}"
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {election.allow_abstain && (
                  <div
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      isAbstaining
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setIsAbstaining(true);
                      setSelectedCandidate('');
                    }}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="candidate"
                        checked={isAbstaining}
                        onChange={() => {
                          setIsAbstaining(true);
                          setSelectedCandidate('');
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">Abstain from Voting</h4>
                        <p className="text-sm text-gray-500">Choose not to vote for any candidate</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleVote}
                  disabled={isVoting || (!selectedCandidate && !isAbstaining)}
                  className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isVoting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Casting Vote...
                    </>
                  ) : (
                    <>
                      <Vote className="h-4 w-4 mr-2" />
                      Cast Vote
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vote Status */}
        {userVote && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-green-800">Vote Cast Successfully</h3>
                <p className="text-sm text-green-600">
                  {userVote.is_abstain 
                    ? 'You have chosen to abstain from voting.'
                    : `You voted for ${candidates.find(c => c.id === userVote.candidate_id)?.first_name} ${candidates.find(c => c.id === userVote.candidate_id)?.last_name}.`
                  }
                </p>
                <p className="text-xs text-green-500 mt-1">
                  Voted on {formatDate(userVote.voted_at)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Candidates List */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Candidates</h3>
              <div className="flex space-x-2">
                <button
                  onClick={fetchCandidates}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </button>
                {(election.status === 'completed' || election.status === 'active') && (
                  <button
                    onClick={fetchResults}
                    className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    View Results
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {candidates.map((candidate, index) => (
              <div key={candidate.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-medium text-gray-900">
                        {candidate.first_name} {candidate.last_name}
                        {candidate.is_winner && (
                          <Trophy className="inline h-5 w-5 text-yellow-500 ml-2" />
                        )}
                      </h4>
                      <p className="text-sm text-gray-500">
                        Member ID: {candidate.member_id} • {candidate.email}
                      </p>
                      {candidate.campaign_slogan && (
                        <p className="text-sm text-gray-600 mt-1">
                          "{candidate.campaign_slogan}"
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {candidate.votes_received} votes
                    </div>
                    <div className="text-sm text-gray-500">
                      {candidate.vote_percentage.toFixed(1)}%
                    </div>
                    {showResults && (
                      <div className="text-xs text-gray-400">
                        Rank #{index + 1}
                      </div>
                    )}
                  </div>
                </div>
                {candidate.manifesto && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Manifesto</h5>
                    <p className="text-sm text-gray-600">{candidate.manifesto}</p>
                  </div>
                )}
                {candidate.qualifications && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Qualifications</h5>
                    <p className="text-sm text-gray-600">{candidate.qualifications}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        {showResults && results && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Election Results</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{results.total_votes}</div>
                  <div className="text-sm text-gray-500">Total Votes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{results.voter_turnout.toFixed(1)}%</div>
                  <div className="text-sm text-gray-500">Voter Turnout</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{results.abstain_votes}</div>
                  <div className="text-sm text-gray-500">Abstain Votes</div>
                </div>
              </div>

              {results.winner && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <Trophy className="h-6 w-6 text-yellow-500 mr-2" />
                    <div>
                      <h4 className="text-lg font-medium text-yellow-800">Winner</h4>
                      <p className="text-yellow-700">
                        {results.winner.first_name} {results.winner.last_name} with {results.winner.votes_received} votes
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {results.is_tie && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AlertCircle className="h-6 w-6 text-orange-500 mr-2" />
                    <div>
                      <h4 className="text-lg font-medium text-orange-800">Tie Result</h4>
                      <p className="text-orange-700">
                        Multiple candidates received the same number of votes
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElectionDetailPage;
